import React from 'react';
import AppDropdown from '../AppDropdown';
import AppDateInput from '../AppDateInput';

export default function ContactInfoCard({ 
  value = {}, 
  onChange, 
  groupOptions = [],
  groupLabel = 'Cust. Group',
  interestLabel = 'Interest'
}) {
  const patch = (field, val) => {
    if (onChange) {
      onChange({ ...value, [field]: val });
    }
  };

  const defaultGroups = [
    { value: 'Select', label: 'Select' },
    { value: 'Reserves & Surplus', label: 'Reserves & Surplus' },
    { value: 'Sundry Creditors', label: 'Sundry Creditors' },
    { value: 'Bank Accounts', label: 'Bank Accounts' }
  ];

  const resolvedGroupOptions = groupOptions.length > 0 ? groupOptions : defaultGroups;

  const interestPeriodOptions = [
    { value: 'Year', label: 'Year' },
    { value: 'Month', label: 'Month' }
  ];

  return (
    <div className="rounded-2xl border border-outline-variant/30 bg-surface-container-lowest overflow-hidden shadow-xs">
      {/* Header */}
      <div className="bg-surface-container-low/60 px-6 py-3 border-b border-outline-variant/25 text-sm font-bold text-on-surface">
        Contact Information
      </div>

      {/* Inputs */}
      <div className="p-6 flex flex-col gap-4">
        
        {/* Cust. Group */}
        <div className="grid grid-cols-12 gap-4 items-center">
          <span className="col-span-3 text-xs font-black uppercase tracking-wider text-on-surface-variant">{groupLabel}</span>
          <AppDropdown
            value={value.custGroup || 'Select'}
            onChange={(val) => patch('custGroup', val)}
            options={resolvedGroupOptions}
            className="col-span-9"
            triggerClassName="h-10 bg-surface-container-low"
          />
        </div>

        {/* Person */}
        <label className="grid grid-cols-12 gap-4 items-center">
          <span className="col-span-3 text-xs font-black uppercase tracking-wider text-on-surface-variant">Person</span>
          <input
            type="text"
            value={value.person || ''}
            onChange={(e) => patch('person', e.target.value)}
            placeholder="Contact Person..."
            className="col-span-9 h-10 rounded-xl border border-outline-variant/30 bg-surface-container-low px-4 text-sm text-on-surface outline-none focus:ring-2 focus:ring-primary/20 transition"
          />
        </label>

        {/* Designation */}
        <label className="grid grid-cols-12 gap-4 items-center">
          <span className="col-span-3 text-xs font-black uppercase tracking-wider text-on-surface-variant">Designation</span>
          <input
            type="text"
            value={value.designation || ''}
            onChange={(e) => patch('designation', e.target.value)}
            placeholder="Designation..."
            className="col-span-9 h-10 rounded-xl border border-outline-variant/30 bg-surface-container-low px-4 text-sm text-on-surface outline-none focus:ring-2 focus:ring-primary/20 transition"
          />
        </label>

        {/* Email ID */}
        <label className="grid grid-cols-12 gap-4 items-center">
          <span className="col-span-3 text-xs font-black uppercase tracking-wider text-on-surface-variant">Email ID</span>
          <input
            type="email"
            value={value.emailId || ''}
            onChange={(e) => patch('emailId', e.target.value)}
            placeholder="email@example.com"
            className="col-span-9 h-10 rounded-xl border border-outline-variant/30 bg-surface-container-low px-4 text-sm text-on-surface outline-none focus:ring-2 focus:ring-primary/20 transition"
          />
        </label>

        {/* CCEmail ID */}
        <label className="grid grid-cols-12 gap-4 items-center">
          <span className="col-span-3 text-xs font-black uppercase tracking-wider text-on-surface-variant">CCEmail ID</span>
          <input
            type="email"
            value={value.ccEmailId || ''}
            onChange={(e) => patch('ccEmailId', e.target.value)}
            placeholder="cc@example.com"
            className="col-span-9 h-10 rounded-xl border border-outline-variant/30 bg-surface-container-low px-4 text-sm text-on-surface outline-none focus:ring-2 focus:ring-primary/20 transition"
          />
        </label>

        {/* Address Fields: Bill To & Ship To */}
        <div className="grid grid-cols-12 gap-4 items-start">
          <span className="col-span-3 text-xs font-black uppercase tracking-wider text-on-surface-variant pt-2.5">Address</span>
          <div className="col-span-9 flex flex-col gap-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="grid gap-1">
                <span className="text-[10px] font-bold text-on-surface-variant">Bill To</span>
                <textarea
                  value={value.billTo || ''}
                  onChange={(e) => patch('billTo', e.target.value)}
                  placeholder="Billing Address..."
                  rows={3}
                  className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-low p-3.5 text-sm text-on-surface outline-none focus:ring-2 focus:ring-primary/20 transition resize-y"
                />
              </label>
              <label className="grid gap-1">
                <span className="text-[10px] font-bold text-on-surface-variant">Ship To</span>
                <textarea
                  value={value.shipTo || ''}
                  onChange={(e) => patch('shipTo', e.target.value)}
                  placeholder="Shipping Address..."
                  rows={3}
                  className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-low p-3.5 text-sm text-on-surface outline-none focus:ring-2 focus:ring-primary/20 transition resize-y"
                />
              </label>
            </div>
            <button
              type="button"
              onClick={() => {
                // Mimics 'Add new Address' action
                patch('additionalAddresses', [...(value.additionalAddresses || []), '']);
              }}
              className="text-xs font-bold text-primary hover:underline self-start mt-1 flex items-center gap-1 cursor-pointer"
            >
              <span className="material-symbols-outlined text-sm">add</span>
              Add new Address
            </button>
          </div>
        </div>

        {/* Mobile & Phone */}
        <div className="grid grid-cols-12 gap-4 items-center">
          <span className="col-span-3 text-xs font-black uppercase tracking-wider text-on-surface-variant">Mobile</span>
          <div className="col-span-9 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input
              type="text"
              value={value.mobile || ''}
              onChange={(e) => patch('mobile', e.target.value)}
              placeholder="Mobile number..."
              className="h-10 w-full rounded-xl border border-outline-variant/30 bg-surface-container-low px-4 text-sm text-on-surface outline-none focus:ring-2 focus:ring-primary/20 transition"
            />
            <div className="flex items-center gap-3">
              <span className="text-xs font-black uppercase tracking-wider text-on-surface-variant whitespace-nowrap shrink-0">Phone</span>
              <input
                type="text"
                value={value.phone || ''}
                onChange={(e) => patch('phone', e.target.value)}
                placeholder="Phone..."
                className="h-10 w-full rounded-xl border border-outline-variant/30 bg-surface-container-low px-4 text-sm text-on-surface outline-none focus:ring-2 focus:ring-primary/20 transition"
              />
            </div>
          </div>
        </div>

        {/* Contact Info Text Area */}
        <label className="grid grid-cols-12 gap-4 items-start">
          <span className="col-span-3 text-xs font-black uppercase tracking-wider text-on-surface-variant pt-2.5">Contact Info</span>
          <textarea
            value={value.contactInfo || ''}
            onChange={(e) => patch('contactInfo', e.target.value)}
            placeholder="Additional contact instructions or history..."
            rows={2}
            className="col-span-9 rounded-xl border border-outline-variant/30 bg-surface-container-low p-3.5 text-sm text-on-surface outline-none focus:ring-2 focus:ring-primary/20 transition resize-y"
          />
        </label>

        {/* Credit Days & Interest */}
        <div className="grid grid-cols-12 gap-4 items-center">
          <span className="col-span-3 text-xs font-black uppercase tracking-wider text-on-surface-variant">Credit days</span>
          <div className="col-span-9 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input
              type="number"
              value={value.creditDays ?? 0}
              onChange={(e) => patch('creditDays', parseInt(e.target.value) || 0)}
              placeholder="0"
              className="h-10 w-full rounded-xl border border-outline-variant/30 bg-surface-container-low px-4 text-sm text-on-surface outline-none focus:ring-2 focus:ring-primary/20 transition"
            />
            <div className="flex items-center gap-2">
              <span className="text-xs font-black uppercase tracking-wider text-on-surface-variant whitespace-nowrap shrink-0">{interestLabel}</span>
              <div className="flex items-center w-full rounded-xl border border-outline-variant/30 bg-surface-container-low overflow-hidden pr-1">
                <input
                  type="number"
                  value={value.interestRate ?? 0}
                  onChange={(e) => patch('interestRate', parseFloat(e.target.value) || 0)}
                  placeholder="0"
                  className="h-10 w-full bg-transparent px-3 text-sm text-on-surface outline-none border-r border-outline-variant/20"
                />
                <span className="px-2.5 text-xs font-bold text-on-surface-variant select-none">%</span>
                <AppDropdown
                  value={value.interestPeriod || 'Year'}
                  onChange={(val) => patch('interestPeriod', val)}
                  options={interestPeriodOptions}
                  className="w-[110px]"
                  triggerClassName="h-8 border-0 shadow-none bg-transparent py-0 pr-1 text-xs"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Birth Date & Anniversary */}
        <div className="grid grid-cols-12 gap-4 items-center">
          <span className="col-span-3 text-xs font-black uppercase tracking-wider text-on-surface-variant">Birth Date</span>
          <div className="col-span-9 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <AppDateInput
              value={value.birthDate || ''}
              onChange={(val) => patch('birthDate', val)}
              inputClassName="h-10 bg-surface-container-low rounded-xl border-outline-variant/30"
            />
            <div className="flex items-center gap-2">
              <span className="text-xs font-black uppercase tracking-wider text-on-surface-variant whitespace-nowrap shrink-0">Anniversary</span>
              <AppDateInput
                value={value.anniversary || ''}
                onChange={(val) => patch('anniversary', val)}
                inputClassName="h-10 bg-surface-container-low rounded-xl border-outline-variant/30"
              />
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
